import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import LeaseModal, { LeaseModalSubmitData } from '../Leases/LeaseModal';
import { useTenants } from '../../hooks/tenants/useTenants';
import { useLeaseMutations } from '../../hooks/leases';
import { ParkingQueueMutations, UseParkingQueueReturn } from '../../hooks/parkingQueue';
import { useQueueDeviation } from '../../hooks/parkingQueue/useQueueDeviation';
import { SpotTarget } from '../../api/parkingQueueApi';
import { suggestedLeaseStart } from '../../Helpers/parkingQueueUtils';
import { parkingAreaDisplayName } from '../../Helpers/parkingUtils';
import { translate } from '../../types/EnumTranslations';
import { LeaseObjectType } from '../../types/Enums';
import { ParkingQueueEntry } from '../../types/parkingQueue';
import DeviationNotice from './DeviationNotice';

interface Props {
    entry: ParkingQueueEntry;
    queue: UseParkingQueueReturn;
    mutations: ParkingQueueMutations;
    /** The spot from `?spot=`: preselected (still changeable) when the entry wants its area. */
    initialSpot?: SpotTarget;
    onHide: () => void;
}

interface SpotValues { buildingKey: string; areaKey: string; spotKey: string; reason: string }

/**
 * "Nytt hyresavtal" from a köplats: Välj plats (the entry's wanted areas,
 * with the deviation check and its required reason), then the ordinary
 * LeaseModal locked to that spot. Only once the lease is saved does the queue
 * write `deviation` + `leaseLinked` and close the entry (`linkLease`); an
 * abandoned form writes nothing. A failed link leaves the lease in place and
 * the error on the detail modal.
 */
/** Reports the deviation check for the spot picked in the form; `null` while loading or without a spot. */
const DeviationWatcher: React.FC<{ entryKey: string; onResolved: (ahead: ParkingQueueEntry[] | null) => void }> = ({ entryKey, onResolved }) => {
    const { values } = useFormikContext<SpotValues>();
    const ahead = useQueueDeviation(entryKey, values);
    useEffect(() => { onResolved(ahead); }, [ahead, onResolved]);
    return null;
};

const QueueLeaseFlow: React.FC<Props> = ({ entry, queue, mutations, initialSpot, onHide }) => {
    const { getTenantName } = useTenants();
    const [chosen, setChosen] = useState<(SpotTarget & { reason?: string }) | null>(null);
    const [ahead, setAhead] = useState<ParkingQueueEntry[] | null>(null);
    const leaseMutations = useLeaseMutations(queue.refresh);

    const wantedBuildings = useMemo(() => Array.from(new Set(Object.values(entry.wantedAreas))), [entry.wantedAreas]);
    const initial = useMemo<SpotValues>(() => {
        const preselect = initialSpot && entry.wantedAreas[initialSpot.areaKey] === initialSpot.buildingKey ? initialSpot : undefined;
        return {
            buildingKey: preselect?.buildingKey ?? (wantedBuildings.length === 1 ? wantedBuildings[0] : ''),
            areaKey: preselect?.areaKey ?? '',
            spotKey: preselect?.spotKey ?? '',
            reason: '',
        };
    }, [initialSpot, entry.wantedAreas, wantedBuildings]);
    const buildingOptions = wantedBuildings
        .filter(b => queue.areasByBuilding[b])
        .map(b => ({ key: b, name: queue.buildingName(b) }))
        .sort((a, b) => a.name.localeCompare(b.name, 'sv'));

    const schema = Yup.object({
        buildingKey: Yup.string().required('Välj byggnad'),
        areaKey: Yup.string().required('Välj område'),
        spotKey: Yup.string().required('Välj plats'),
        reason: ahead && ahead.length > 0 ? Yup.string().trim().required('Ange anledning') : Yup.string(),
    });

    if (chosen) {
        const buildingLeases = queue.leases.filter(l => l.buildingKey === chosen.buildingKey);
        const spotLeases = buildingLeases.filter(l => l.object.type === LeaseObjectType.ParkingSpot && l.object.areaKey === chosen.areaKey && l.object.key === chosen.spotKey);
        const onLeaseSubmit = async (data: LeaseModalSubmitData) => {
            const lease = await leaseMutations.createLeaseWithContracts(data.input, data.filesToUpload);
            try {
                await mutations.linkLease(entry.key, { ...chosen, leaseKey: lease.key });
            } catch {
                // The lease exists; the link error stays on the detail modal (mutations.error).
            }
            onHide();
        };
        return (
            <LeaseModal show onHide={onHide} onSubmit={onLeaseSubmit} buildingKey={chosen.buildingKey}
                areas={Object.values(queue.areasByBuilding[chosen.buildingKey] ?? {})} buildingLeases={buildingLeases}
                rentalUnits={Object.values(queue.unitsByBuilding[chosen.buildingKey] ?? {})}
                lockedSpot={{ areaKey: chosen.areaKey, spotKey: chosen.spotKey }} initialTenantKey={entry.tenantKey}
                initialStartDate={suggestedLeaseStart(spotLeases)} submitting={leaseMutations.submitting || mutations.submitting} />
        );
    }

    return (
        <Modal show onHide={onHide}>
            <Formik<SpotValues> initialValues={initial} validationSchema={schema}
                onSubmit={v => setChosen({ buildingKey: v.buildingKey, areaKey: v.areaKey, spotKey: v.spotKey, reason: v.reason.trim() || undefined })}>
                {({ values, errors, touched, handleSubmit, setFieldValue }) => {
                    const areaOptions = Object.values(queue.areasByBuilding[values.buildingKey] ?? {})
                        .filter(a => entry.wantedAreas[a.key] === values.buildingKey);
                    const spotOptions = Object.values(queue.areasByBuilding[values.buildingKey]?.[values.areaKey]?.spots ?? {})
                        .sort((a, b) => a.number.localeCompare(b.number, 'sv', { numeric: true }));
                    return (
                        <Form noValidate onSubmit={handleSubmit}>
                            <DeviationWatcher entryKey={entry.key} onResolved={setAhead} />
                            <Modal.Header closeButton><Modal.Title>Nytt hyresavtal</Modal.Title></Modal.Header>
                            <Modal.Body>
                                {mutations.error && <Alert variant="danger">{mutations.error}</Alert>}
                                <p className="mb-3">Sökande: <strong>{getTenantName(entry.tenantKey)}</strong></p>
                                <Form.Group className="mb-3" controlId="queue-lease-building">
                                    <Form.Label>Byggnad *</Form.Label>
                                    <Form.Select value={values.buildingKey} isInvalid={touched.buildingKey && !!errors.buildingKey}
                                        onChange={e => { setFieldValue('buildingKey', e.target.value); setFieldValue('areaKey', ''); setFieldValue('spotKey', ''); }}>
                                        <option value="">Välj byggnad…</option>
                                        {buildingOptions.map(b => <option key={b.key} value={b.key}>{b.name}</option>)}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">{errors.buildingKey}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group className="mb-3" controlId="queue-lease-area">
                                    <Form.Label>Område *</Form.Label>
                                    <Form.Select value={values.areaKey} disabled={!values.buildingKey} isInvalid={touched.areaKey && !!errors.areaKey}
                                        onChange={e => { setFieldValue('areaKey', e.target.value); setFieldValue('spotKey', ''); }}>
                                        <option value="">Välj område…</option>
                                        {areaOptions.map(a => <option key={a.key} value={a.key}>{parkingAreaDisplayName(a)}</option>)}
                                    </Form.Select>
                                    <Form.Text muted>Områdena köplatsen önskar.</Form.Text>
                                    <Form.Control.Feedback type="invalid">{errors.areaKey}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group className="mb-3" controlId="queue-lease-spot">
                                    <Form.Label>Plats *</Form.Label>
                                    <Form.Select value={values.spotKey} disabled={!values.areaKey} isInvalid={touched.spotKey && !!errors.spotKey}
                                        onChange={e => setFieldValue('spotKey', e.target.value)}>
                                        <option value="">Välj plats…</option>
                                        {spotOptions.map(s => {
                                            const lease = s.currentLeaseKey ? queue.leases.find(l => l.key === s.currentLeaseKey) : undefined;
                                            const state = !s.currentLeaseKey ? 'ledig' : lease?.endDate ? `uthyrd t.o.m. ${lease.endDate}` : 'uthyrd';
                                            return <option key={s.key} value={s.key}>Plats {s.number} · {translate(s.type)} · {state}</option>;
                                        })}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">{errors.spotKey}</Form.Control.Feedback>
                                </Form.Group>
                                <DeviationNotice ahead={ahead} reason={values.reason} onReasonChange={reason => setFieldValue('reason', reason)}
                                    error={touched.reason ? errors.reason : undefined} idPrefix="queue-lease" />
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={onHide}>Avbryt</Button>
                                <Button type="submit" variant="primary" disabled={!!values.spotKey && ahead === null}>Fortsätt</Button>
                            </Modal.Footer>
                        </Form>
                    );
                }}
            </Formik>
        </Modal>
    );
};

export default QueueLeaseFlow;
